import { Component, DestroyRef, EventEmitter, inject, Input, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { DropdownOption } from '@app/storybook/components/dropdown/dropdown.component';
import {
  UploadIconDialogComponent,
  UploadIconDialogResult,
} from '@app/sharedModule/components/upload-icon-dialog/upload-icon-dialog.component';
import { SmartCartService } from '@pages/engage/smart-cart/smart-cart.service';
import { TimerFormShape, TimerPlacement, TimerPreviewState, TIMER_PRESET_ICON_URLS } from './timer.model';

export interface TimerPlacementOption {
  value: TimerPlacement;
  text: string;
}

@Component({
  selector: 'smart-cart-timer-config',
  templateUrl: './timer-config.component.html',
  styleUrls: ['../shared.scss', './timer-config.component.scss'],
  standalone: false,
})
export class SmartCartTimerConfigComponent {
  @Input({ required: true }) formGroup!: FormGroup<TimerFormShape>;
  @Input() placementOptions: TimerPlacementOption[] = [
    { value: 'inline', text: 'Inline (full width bar)' },
    { value: 'badge', text: 'Badge (icon+text)' },
  ];
  @Input() presetIconUrls: string[] = TIMER_PRESET_ICON_URLS;
  @Input() smartCartId: number | null = null;
  @Input() usageToken = '';
  @Input() enableSubtitle = 'Add urgency with a countdown.';
  @Input() durationHint = 'Timer starts when customer reaches checkout';
  /** Label for the "hide" expiration behavior — differs per widget (Goals: hide bar; Upsell: hide upsell). */
  @Input() hideBehaviorLabel = 'Hide Progress Bar';
  @Input() previewedState: TimerPreviewState | null = null;
  @Output() previewStateChange = new EventEmitter<TimerPreviewState>();

  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);
  private readonly smartCartService = inject(SmartCartService);

  iconUploadError: string | null = null;
  uploadingIcon = false;

  readonly timerUnitOptions: DropdownOption[] = [
    { label: 'Minutes', value: 'minutes' },
    { label: 'Hours', value: 'hours' },
  ];

  /** inline / badge show the full styled appearance; token placements only need expiration behavior. */
  get isStyledPlacement(): boolean {
    const placement = this.formGroup.controls.placement.value;
    return placement === 'inline' || placement === 'badge';
  }

  previewTimer(state: TimerPreviewState): void {
    this.previewStateChange.emit(state);
  }

  onTimerMessageChange(control: FormControl<string>, html: string): void {
    control.setValue(html);
  }

  onTimerIconUrlChange(iconUrl: FormControl<string | File>, value: string | File): void {
    iconUrl.setValue(value);
  }

  onTimerUploadCustomIcon(iconUrl: FormControl<string | File>, customIconUrl: FormControl<string | File>): void {
    this.dialog
      .open<UploadIconDialogComponent, void, UploadIconDialogResult | null>(UploadIconDialogComponent, {
        width: '576px',
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (!result) return;
        this.iconUploadError = null;
        if (this.smartCartId === null) {
          this.iconUploadError = 'Save the cart once before uploading an icon.';
          return;
        }
        this.uploadingIcon = true;
        this.smartCartService
          .uploadIcon(this.smartCartId, this.usageToken, 'timer-icons', result.file)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: ({ url }) => {
              customIconUrl.setValue(url);
              iconUrl.setValue(url);
              this.uploadingIcon = false;
            },
            error: (err) => {
              console.error('[Smart Cart] timer icon upload failed', err);
              this.iconUploadError = 'Upload failed. Please try again.';
              this.uploadingIcon = false;
            },
          });
      });
  }

  onTimerRemoveCustomIcon(iconUrl: FormControl<string | File>, customIconUrl: FormControl<string | File>): void {
    const wasSelected = !!customIconUrl.value && iconUrl.value === customIconUrl.value;
    customIconUrl.setValue('');
    if (wasSelected) {
      iconUrl.setValue('');
    }
  }
}
